import { Clipboard } from '@angular/cdk/clipboard';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { MbsPopupType, ModalService, ModalSettings, ResponseError, TextBreak, Toast, ToastService } from 'mbs-ui-kit';
import { Button } from 'mbs-ui-kit/toast/toast.model';
import { ReportProblemModalComponent } from 'mbs-ui-shared@components/report-problem-modal/report-problem-modal.component';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ConfigurationService } from 'mbs-ui-shared@services/configuration.service';
import { noop } from 'rxjs';
import { unidentifiedErrorText } from '../interceptors/error-handler.interceptor';

@Injectable({ providedIn: 'root' })
export class ErrorHandlerService {
  private config = inject(ConfigurationService);
  private clipboard = inject(Clipboard);
  private i18nPipe = inject(I18NextPipe);
  private injector = inject(Injector);
  private modalService = inject(ModalService);
  private toastService = inject(ToastService);

  #errorHandlerSettings: { isDebug: boolean; delay: number };
  #error: HttpErrorResponse;

  set error(e: HttpErrorResponse) {
    this.#error = e;
  }

  get error(): HttpErrorResponse {
    return this.#error;
  }

  get isDebug(): boolean {
    return this.#errorHandlerSettings?.isDebug;
  }

  get #traceId(): string {
    return this.error?.headers?.get('trace-id');
  }

  get #url(): string {
    return this.error?.url;
  }

  // Integration common error codes
  private connectWiseIntegrationError = 'https://mspbackups.com/errors/mbs/4141';
  private haloPSAIntegrationError = 'https://mspbackups.com/errors/mbs/4143';

  private integrationErrorTypes = [this.connectWiseIntegrationError, this.haloPSAIntegrationError];

  // Lazy getter to break the circular dependency:
  // ErrorHandlerInterceptor → ErrorHandlerService → AuthFacade → (via HttpClient) → ErrorHandlerInterceptor
  private get auth(): AuthFacade {
    return this.injector.get(AuthFacade);
  }

  constructor() {
    const config = this.config;

    this.#errorHandlerSettings = config.get('errorHandlerSettings');
  }

  showToastWithDetails(options: { error?: ResponseError; title?: string; modalText?: string; toastText?: string }, url?: string): void {
    const { error, title, modalText, toastText } = options || {};

    if (this.integrationErrorTypes.includes(error?.type)) {
      this.showDetailedToast(error, title, modalText, toastText);
    } else {
      this.showToast(toastText || error?.detail, title || error?.title, modalText || error?.detail, url, true);
    }
  }

  showSubscriptionErrorToast(): void {
    this.toastService.error(this.i18nPipe.transform('shared:licenses.subscribeError', { url: this.auth.subscriptionUrl }));
  }

  showErrorToast(message?: string, title?: string, url?: string): void {
    this.showToast(message, title, '', url);
  }

  showDebugToast(error?: HttpErrorResponse): void {
    const errorPayload = error?.error;
    const skipErrorCodes = [420, 401];
    if (!this.#errorHandlerSettings?.isDebug || !this.#traceId || skipErrorCodes.includes(error?.status)) {
      return;
    }

    this.toastService.toast({
      icon: false,
      header: errorPayload?.title || unidentifiedErrorText,
      content: this.#getDebugText(errorPayload?.detail),
      delay: this.#errorHandlerSettings?.delay || 60000,
      showClose: true,
      type: MbsPopupType.info,
      textBreak: TextBreak.content,
      preventClose: true,
      buttons: this.#getCopyButtons()
    });
  }

  #getDebugText(detail: string): string {
    return `
      <b>Detail: ${detail || ''}</b>
      <br />
      <b>URL:</b> ${this.#url}
      <br />
      <b>Trace ID:</b> ${this.#traceId}
      <br />
      <b class="text-warning">DEBUG:</b> This alert is not displayed on production.
    `;
  }

  #getCopyButtons(): Array<Button> {
    return [
      { label: 'Copy URL', clickHandler: () => this.clipboard.copy(this.#url) },
      {
        label: 'Open trace',
        clickHandler: () => window.open(`https://jaeger.eks.stage.mspbackups.com/trace/${this.#traceId}`, '_blank').focus()
      },
      { label: 'Copy trace ID', clickHandler: () => this.clipboard.copy(this.#traceId) }
    ];
  }

  isRemoteCommand = (error: HttpErrorResponse): boolean => !!error?.url?.includes('/remotecommand');

  // Private section
  private showToast(message: string, title?: string, detailMessage?: string, url?: string, needLimitedContainer = false): void {
    this.toastService.toast(
      new Toast({
        needLimitedContainer,
        header: title,
        content: message,
        type: MbsPopupType.danger,
        icon: true,
        buttons: [
          {
            label: this.i18nPipe.transform('shared:buttons.reportProblem'),
            clickHandler: () => this.reportProblem(message, title, detailMessage, url)
          }
        ]
      })
    );
  }

  private reportProblem(message: string, title?: string, detailMessage?: string, url?: string): void {
    const modal = this.modalService.openRef(ReportProblemModalComponent);

    (modal.componentInstance as ReportProblemModalComponent).initialErrorData = {
      title,
      url,
      message: detailMessage || message,
      traceId: this.#traceId
    };
    modal.result.catch(noop);
  }

  private showDetailedToast(error?: ResponseError, title?: string, modalText?: string, toastText?: string): void {
    const textMessage = toastText || error?.detail;

    const modalBody =
      error?.detail && error?.learnMoreLink
        ? `<div>${textMessage}</div> <a href="${error.learnMoreLink}" target="_blank">Learn more</a>`
        : null;

    this.toastService.toast(
      new Toast({
        header: title || error?.title,
        content: textMessage,
        type: MbsPopupType.danger,
        needEllipsis: true,
        icon: true,
        buttons: [
          {
            clickHandler: function (): void {
              this.openModalWithDetail(modalBody || modalText || error?.detail, title || error?.title);
            }.bind(this)
          }
        ]
      })
    );
  }

  private openModalWithDetail(content: string, title: string): void {
    const settings: ModalSettings = {
      header: { title },
      footer: {
        okButton: {
          show: false
        },
        cancelButton: {
          text: this.i18nPipe.transform('shared:buttons.close')
        }
      }
    };

    this.modalService.open(settings, content).finally(noop);
  }
}
